import { runBatch } from './document';

export async function getSelectionText(): Promise<string> {
  let text = '';
  await runBatch(async (context) => {
    const range = context.document.getSelection();
    range.load('text');
    await context.sync();
    text = range.text;
  });
  return text;
}

export async function hasSelection(): Promise<boolean> {
  const text = await getSelectionText();
  return text.trim().length > 0;
}

/** A selection split into the spacing at its edges and the text in between. */
export interface EdgeSpacing {
  /** Spaces/tabs the user accidentally selected before the text. */
  lead: string;
  /** The selection without its edge spacing — what the action should wrap. */
  core: string;
  /** Spaces/tabs after the text. */
  trail: string;
}

/**
 * Splits off the spacing at the edges of a selection.
 *
 * Double-clicking a word in Word selects the trailing space with it, and hand
 * selections regularly grab one at either end. Every action that REPLACES the
 * selection (annotation, placeholder, yellow brackets, defined term) would
 * otherwise swallow that space — "hello [V14: foo]world" — or pull it inside
 * the brackets. Actions therefore wrap `core` and re-emit `lead`/`trail`
 * unchanged around the result, which is the same as having selected the text
 * without the spacing in the first place.
 *
 * Only spaces and tabs count as edge spacing. Paragraph marks (\r) are NOT
 * trimmed: re-emitting one would create a new paragraph and lose the original's
 * numbering/style. Non-breaking spaces stay too — they are content in legal
 * text ("§ 5"), not accidental spacing.
 */
export function splitEdgeSpacing(text: string): EdgeSpacing {
  const match = /^([ \t]*)([\s\S]*?)([ \t]*)$/.exec(text ?? '');
  if (!match) return { lead: '', core: text ?? '', trail: '' };
  const [, lead, core, trail] = match;
  return { lead, core, trail };
}
